import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  Loader,
  ModalUI,
  Selector,
  showNotification,
  TableListV2,
  Tag,
  useQueryFetch
} from 'sonata-react-components';

import { CsvDropzone } from '@/components/common/CsvDropzone/CsvDropzone';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import {
  countNotLocatedWhiteBlackListing,
  processAllWhiteBlackListing,
  processWhiteBlackListing,
  uploadWhiteBlackListing
} from '../../../../services/whiteblack.service';
import { notifyGenericEntriesResult } from '../Inventory/helpers/genericEntriesNotifications.helper';
import { getImportEntries, mergeStrictEntries } from '../Inventory/helpers/strictSearches.helper';
import { AppAndSiteMarketplaceImportStyled } from './AppAndSiteMarketplaceImport.styles';
import { GenericImportDetail } from './GenericImportDetail';
import { applyGenericImportPreview, buildGenericImportPreview } from './helpers/genericImport.helper';
import { useGenericCsvFile } from './hooks/useGenericCsvFile';

import type { AppSitePublishersConfiguration } from '@/contexts/Targeting/TargetingContext';
import type { UploadWhiteBlackListing } from '../../../../services/whiteblack.service';
import type { AppSiteMarketplaceType, GenericImportPreview, ImportStatus } from '../AppAndSiteMarketplace.types';

type State = {
  fetchUnlimited: boolean;
  file?: File;
  preview?: GenericImportPreview;
  processId?: string;
  selectors: {
    search_string?: string;
    search_type?: string;
    type?: string;
  };
  uploadValues?: UploadWhiteBlackListing;
};

type Props = {
  entityId: string;
  /** Notified whenever the import status changes. */
  onStatusChange?: (status: ImportStatus) => void;
  sessionId: string;
  type: AppSiteMarketplaceType;
  userId: string;
};

// The import only starts once the column mapping is confirmed (`uploadValues`); before that (or after going back
// to the mapping step) there is nothing to wait for or to confirm.
const getImportStatus = (hasFile: boolean, isMappingConfirmed: boolean, isProcessing: boolean): ImportStatus => {
  if (!hasFile || !isMappingConfirmed) {
    return 'idle';
  }
  return isProcessing ? 'processing' : 'pendingConfirmation';
};

export function AppAndSiteMarketplaceImport({ entityId, onStatusChange, sessionId, type, userId }: Readonly<Props>) {
  const specificTemplateCsv = `${taptap.config.sonataStaticsUrlBase}/samples/appAndSiteMarketplace/exampleWhiteBlackListingSpecific.csv`;
  const genericTemplateCsv = `${taptap.config.sonataStaticsUrlBase}/samples/appAndSiteMarketplace/exampleWhiteBlackListingGeneric.csv`;
  const { formatMessage } = useIntl();
  const importModalRef: any = useRef();
  const confirmModalRef: any = useRef();
  const [{ fetchUnlimited, file, preview, processId, selectors, uploadValues }, setState] = useState<State>({
    fetchUnlimited: false,
    file: undefined,
    preview: undefined,
    processId: undefined,
    selectors: {},
    uploadValues: undefined
  });

  const isSpecific = type === 'specific';

  const {
    setAppSiteConfigurationProps,
    targeting: { appSiteConfiguration }
  } = useTargetingContext();

  const csv = useGenericCsvFile(file, !isSpecific, handleEmptyFile);

  const { data, isFetching } = useQueryFetch(
    ['uploadWhiteBlackListing', file?.name, file?.size, file?.lastModified, entityId, type],
    () => uploadWhiteBlackListing(entityId, file!, sessionId, userId),
    {
      cacheTime: 0,
      enabled: !!file && isSpecific,
      staleTime: 0
    }
  );

  const { data: processData, isLoading: processIsLoading } = useQueryFetch(
    ['processWhiteBlackListing', uploadValues, entityId, type],
    () => processWhiteBlackListing(entityId, uploadValues!, type),
    {
      cacheTime: 0,
      enabled: !!uploadValues && isSpecific,
      staleTime: 0
    }
  );

  const { data: whiteBlackListing, isLoading: processAllIsLoading } = useQueryFetch(
    ['processAllWhiteBlackListing', uploadValues, entityId],
    async () => {
      const [located, notLocatedCount] = await Promise.all([
        processAllWhiteBlackListing(processId!),
        countNotLocatedWhiteBlackListing(processId!)
      ]);
      return { ...located, notLocatedCount };
    },
    {
      cacheTime: 0,
      enabled: !!processId && fetchUnlimited && isSpecific,
      staleTime: 0
    }
  );

  const importStatus = getImportStatus(!!file, !!uploadValues || !!preview, processIsLoading || processAllIsLoading);

  useEffect(() => {
    onStatusChange?.(importStatus);
  }, [importStatus, onStatusChange]);

  // The generic import classifies the entries in the browser, so its search type column is optional (and ignored);
  // it is still offered so the template's columns keep being auto-mapped.
  const isSearchTypeRequired = isSpecific;
  const options = [
    {
      id: 'type',
      label: `${formatMessage({
        defaultMessage: 'Include / Exclude',
        id: 'appAndSiteMarketplace.import.options.include'
      })} *`,
      regex: '.*type.*|.*Include / Exclude \\*.*',
      required: true
    },
    {
      id: 'search_type',
      label: `${formatMessage({
        defaultMessage: 'Search Type',
        id: 'appAndSiteMarketplace.import.options.search'
      })}${isSearchTypeRequired ? ' *' : ''}`,
      regex: '.*search_type.*|.*Search Type.*',
      required: isSearchTypeRequired
    },
    {
      id: 'search_string',
      label: `${formatMessage({
        defaultMessage: 'Search String',
        id: 'appAndSiteMarketplace.import.options.text'
      })} *`,
      regex: '.*search_string.*|.*Search String.*|.*text.*|.*sonata_id.*|.*sonataId.*|.*appSiteKey.*',
      required: true
    }
  ];

  // Columns of the file to map: reported by the backend (specific) or read from the CSV header (generic)
  const columns = isSpecific
    ? (data?.keysToReview ?? []).map(item => ({
        key: item.key,
        label: item.fileCpy,
        matchText: item.key
      }))
    : (csv?.header ?? []).map((header, index) => ({
        key: String(index),
        label: header,
        matchText: header
      }));

  if (columns.length && isEmpty(selectors)) {
    let refenceOptions = structuredClone(options);
    const newSelectorsValue = columns.reduce((acc, item) => {
      return {
        ...acc,
        [item.key]: refenceOptions.find(o => {
          const isMatch = new RegExp(o.regex, 'i').test(item.matchText);
          if (isMatch) {
            refenceOptions = refenceOptions.filter(ro => ro.id !== o.id);
          }
          return isMatch;
        })?.id
      };
    }, {});
    setState(prev => ({
      ...prev,
      selectors: newSelectorsValue
    }));
  }

  if (processData?.processId && !processId) {
    setState(prev => ({
      ...prev,
      processId: processData?.processId
    }));
  }

  // Only the specific flow goes through the backend (the generic import is parsed in the browser)
  if (whiteBlackListing?.items) {
    setState(prev => ({
      ...prev,
      fetchUnlimited: false,
      file: undefined,
      processId: undefined,
      selectors: {},
      uploadValues: undefined
    }));

    const config: AppSitePublishersConfiguration = appSiteConfiguration;
    const included = mergeStrictEntries(
      { publishers: config.whitelistedStrictPublishers, searches: config.whitelistedStrictSearches },
      getImportEntries(whiteBlackListing.items, 'whitelisting')
    );
    const excluded = mergeStrictEntries(
      { publishers: config.blacklistedStrictPublishers, searches: config.blacklistedStrictSearches },
      getImportEntries(whiteBlackListing.items, 'blacklisting')
    );
    setAppSiteConfigurationProps({
      blacklistedStrictPublishers: excluded.publishers,
      blacklistedStrictSearches: excluded.searches,
      whitelistedStrictPublishers: included.publishers,
      whitelistedStrictSearches: included.searches
    });

    showNotification(
      'success',
      formatMessage({
        defaultMessage: 'Your list has been successfully uploaded',
        id: 'appAndSiteMarketplace.import.success'
      })
    );
    if (whiteBlackListing.notLocatedCount > 0) {
      showNotification(
        'warning',
        formatMessage({
          defaultMessage: 'Note: Some of your urls have not been matched with our inventory.',
          id: 'appAndSiteMarketplace.import.notMatched'
        })
      );
    }
  }

  const handleGenericConfirmClick = () => {
    const selectorsByKey: Record<string, string | undefined> = selectors;
    const columnOf = (selectorId: string) => columns.findIndex(item => selectorsByKey[item.key] === selectorId);
    if (!csv) {
      return;
    }
    setState(prev => ({
      ...prev,
      preview: buildGenericImportPreview(
        csv.rows,
        { type: columnOf('type'), value: columnOf('search_string') },
        appSiteConfiguration
      )
    }));
    importModalRef?.current?.closeModal();
    confirmModalRef?.current?.openModal();
  };

  const handleConfirmClick = () => {
    if (!isSpecific) {
      handleGenericConfirmClick();
      return;
    }
    if (data) {
      const values = structuredClone(data);
      values.keysToReview = data.keysToReview.map(item => ({
        ...item,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        type: selectors[item.key]
      }));
      setState(prev => ({
        ...prev,
        uploadValues: values
      }));
      importModalRef?.current?.closeModal();
    }
  };

  const handleGenericSaveClick = () => {
    if (!preview) {
      return;
    }
    setAppSiteConfigurationProps(applyGenericImportPreview(appSiteConfiguration, preview));
    showNotification(
      'success',
      formatMessage({
        defaultMessage: 'Your list has been successfully uploaded',
        id: 'appAndSiteMarketplace.import.success'
      })
    );
    notifyGenericEntriesResult(formatMessage, {
      invalidCount: preview.invalidCount,
      skippedSubdomains: preview.skippedSubdomains
    });
    confirmModalRef?.current?.closeModal();
    handleResetClick();
  };

  const handleSaveClick = () => {
    if (!isSpecific) {
      handleGenericSaveClick();
      return;
    }
    confirmModalRef?.current?.closeModal();
    setState(prev => ({
      ...prev,
      fetchUnlimited: true
    }));
  };

  // Back to the column-mapping step of the import modal, keeping the file and the selected columns
  const handleConfirmGoBackClick = () => {
    confirmModalRef?.current?.closeModal();
    setState(prev => ({
      ...prev,
      preview: undefined,
      processId: undefined,
      uploadValues: undefined
    }));
    importModalRef?.current?.openModal();
  };

  const handleSelectorChange = (key: string, value: string) => {
    setState(prevState => ({
      ...prevState,
      selectors: {
        ...prevState.selectors,
        [key]: value
      }
    }));
  };

  const renderUpload = () => (
    <CsvDropzone
      inputId="appAndSiteMarketplaceImportCsv"
      onFile={newFile => setState(prev => ({ ...prev, file: newFile, selectors: {} }))}
      templateLabel={formatMessage({
        defaultMessage: 'Download Template',
        id: 'appAndSiteMarketplace.import.buttons.download'
      })}
      templateUrl={type === 'generic' ? genericTemplateCsv : specificTemplateCsv}
      uploadLabel={formatMessage({
        defaultMessage: 'Upload File',
        id: 'appAndSiteMarketplace.import.buttons.upload'
      })}
    />
  );

  const colsValues = Object.values(selectors);
  const requiredColumns = options.filter(option => option.required).every(option => colsValues.includes(option.id));

  const renderConfirm = () => (
    <>
      <div className="appAndSiteMarketplaceImport__required">
        <span className="appAndSiteMarketplaceImport__requiredAsterisk">*</span>
        {formatMessage({
          defaultMessage: 'Indicates a required field',
          id: 'appAndSiteMarketplace.import.requiredLegend'
        })}
      </div>
      <div className="appAndSiteMarketplaceImport__columns">
        <div className="appAndSiteMarketplaceImport__columnsDescription">
          <span>
            {formatMessage({
              defaultMessage: 'Almost Done!',
              id: 'appAndSiteMarketplace.import.descriptions.done'
            })}
          </span>
          {formatMessage({
            defaultMessage: 'Just indicate which',
            id: 'appAndSiteMarketplace.import.descriptions.just'
          })}
          <span>
            {formatMessage({
              defaultMessage: 'columns',
              id: 'appAndSiteMarketplace.import.descriptions.columns'
            })}
          </span>
          {formatMessage({
            defaultMessage: 'to use from your file',
            id: 'appAndSiteMarketplace.import.descriptions.use'
          })}
        </div>
        <div className="appAndSiteMarketplaceImport__columnsSelectors">
          {columns.map(item => (
            <Selector
              id={item.key}
              key={item.key}
              label={`${formatMessage({
                defaultMessage: 'Column',
                id: 'appAndSiteMarketplace.import.column'
              })} ${item.label}`}
              onChange={(val: { id: string }) => handleSelectorChange(item.key, val.id)}
              options={options.filter(
                i => !Object.entries(selectors).some(([key, id]) => key !== item.key && id === i.id)
              )}
              placeholder={`${formatMessage({
                defaultMessage: 'Select',
                id: 'common.select'
              })} ${item.label}`}
              type="simple"
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              value={options.filter(i => i.id === selectors[item.key])}
            />
          ))}
        </div>
      </div>
      <div className="appAndSiteMarketplaceImport__footer">
        <ButtonUI
          icon="fa-solid fa-arrow-left"
          id="appAndSiteMarketplaceImportGoBack"
          onClick={handleResetClick}
          text={formatMessage({
            defaultMessage: 'Go back',
            id: 'appAndSiteMarketplace.import.buttons.goBack'
          })}
          type="secondary"
        />
        <ButtonUI
          disabled={!requiredColumns}
          id="appAndSiteMarketplaceImportConfirm"
          onClick={handleConfirmClick}
          text={formatMessage({
            defaultMessage: 'Confirm',
            id: 'common.confirm'
          })}
          type="primary"
        />
      </div>
    </>
  );

  const getTag = (id: string, tagType: string) => {
    const isLocated = tagType === 'located';
    return (
      <Tag id={`tag-${id}`} status={isLocated ? 'ok' : 'wrong'}>
        {isLocated
          ? formatMessage({
              defaultMessage: 'Located',
              id: 'appAndSiteMarketplace.import.located'
            })
          : formatMessage({
              defaultMessage: 'Not Located',
              id: 'appAndSiteMarketplace.import.notLocated'
            })}
      </Tag>
    );
  };

  function handleResetClick() {
    setState(prev => ({
      ...prev,
      fetchUnlimited: false,
      file: undefined,
      preview: undefined,
      processId: undefined,
      selectors: {},
      uploadValues: undefined
    }));
  }

  function handleEmptyFile() {
    showNotification(
      'error',
      formatMessage({
        defaultMessage: 'The file is empty or could not be read. Please check it and try again.',
        id: 'appAndSiteMarketplace.import.emptyFile'
      })
    );
    handleResetClick();
  }

  const getImportButton = () => {
    if (!file) {
      return (
        <div className="appAndSiteMarketplaceImport__buttonContainer">
          <ButtonUI
            className="appAndSiteMarketplaceImport__buttonUpload"
            icon="fa-solid fa-upload"
            id="appAndSiteMarketplaceUpload"
            onClick={() => importModalRef?.current?.openModal()}
            shape="squareRounded"
            size="small"
            text={formatMessage({
              defaultMessage: 'Upload your own list',
              id: 'appAndSiteMarketplace.import.buttonUpload.text'
            })}
            type="secondary"
          />
        </div>
      );
    }
    if (processIsLoading || processAllIsLoading) {
      return (
        <div className="appAndSiteMarketplaceImport__buttonLoading">
          <i className="fa-solid fa-spinner fa-spin appAndSiteMarketplaceImport__buttonLoadingSpinner" />
          <span className="appAndSiteMarketplaceImport__buttonLoadingFile">{file.name}</span>
          {formatMessage({
            defaultMessage: 'Loading Results...',
            id: 'appAndSiteMarketplace.import.loadingResults'
          })}
          <ButtonUI
            className="appAndSiteMarketplaceImport__buttonLoadingIcon"
            icon="fa-regular fa-trash"
            id="appAndSiteMarketplaceLoadingReset"
            onClick={() => handleResetClick()}
            shape="link"
            type="secondary"
          />
        </div>
      );
    }
    return (
      <div className="appAndSiteMarketplaceImport__fileContainer">
        <span className="appAndSiteMarketplaceImport__fileDisclaimer">
          <i className="fa-regular fa-triangle-exclamation" />
          {formatMessage({
            defaultMessage: 'You must click on the edit button to confirm the upload',
            id: 'appAndSiteMarketplace.import.fileDisclaimer'
          })}
        </span>
        <div className="appAndSiteMarketplaceImport__buttonConfirm">
          <i className="fa-regular fa-circle-check appAndSiteMarketplaceImport__buttonConfirmIcon" />
          <span className="appAndSiteMarketplaceImport__buttonConfirmFile">{file.name}</span>
          <ButtonUI
            className="appAndSiteMarketplaceImport__buttonConfirmEdit"
            icon="fa-regular fa-pen-to-square"
            id={`appAndSiteMarketplaceConfirmEdit-${entityId}`}
            onClick={() => confirmModalRef?.current?.openModal()}
            shape="link"
            type="secondary"
          />
          <ButtonUI
            className="appAndSiteMarketplaceImport__buttonConfirmDelete"
            icon="fa-regular fa-trash"
            id={`appAndSiteMarketplaceConfirmDelete-${entityId}`}
            onClick={() => handleResetClick()}
            shape="link"
            type="secondary"
          />
        </div>
      </div>
    );
  };

  return (
    <AppAndSiteMarketplaceImportStyled className="appAndSiteMarketplaceImport">
      {getImportButton()}
      <ModalUI
        ref={importModalRef}
        title={formatMessage({
          defaultMessage: 'Import your Include/Exclude List',
          id: 'appAndSiteMarketplace.import.title'
        })}
        width="805px"
      >
        {isFetching || (!isSpecific && !!file && !csv) ? (
          <Loader
            className="appAndSiteMarketplaceImport__loader"
            id="appAndSiteMarketplaceImportLoader"
            type="landing"
            variant="default"
          >
            <Loader.Title className="appAndSiteMarketplaceImport__loaderTitle">
              {formatMessage({
                defaultMessage: 'Loading, please wait...',
                id: 'appAndSiteMarketplace.import.loading'
              })}
            </Loader.Title>
          </Loader>
        ) : columns.length ? (
          renderConfirm()
        ) : (
          renderUpload()
        )}
      </ModalUI>
      <ModalUI
        ref={confirmModalRef}
        title={formatMessage({
          defaultMessage: 'List imported Detail',
          id: 'appAndSiteMarketplace.import.confirmTitle'
        })}
        width="805px"
      >
        {isSpecific ? (
          <TableListV2
            className="appAndSiteMarketplaceImport__details"
            columns={[
              {
                Header: formatMessage({
                  defaultMessage: 'URL',
                  id: 'appAndSiteMarketplace.import.header.url'
                }),
                accessor: 'url'
              },
              {
                Header: formatMessage({
                  defaultMessage: 'Sites',
                  id: 'appAndSiteMarketplace.import.header.sites'
                }),
                accessor: 'sites'
              },
              {
                Header: formatMessage({
                  defaultMessage: 'Type',
                  id: 'appAndSiteMarketplace.import.header.type'
                }),
                accessor: 'type'
              }
            ]}
            config={{
              pagination: {
                async: {
                  countData: res => res.totalItems || 0,
                  formatRequestArgs: (page: number, size: number) => ({
                    method: 'GET',
                    params: {
                      limit: size,
                      order: 'desc',
                      page: page + 1,
                      sortBy: 'status'
                    }
                  }),
                  parseData: res =>
                    res?.items?.map((item: any) => ({
                      id: item._id,
                      sites: item.keys.filter(Boolean).length,
                      type: getTag(item._id, item.statusMsg),
                      url: item.searchString
                    })) || [],
                  url: `${taptap.config.backendUrlBase}batch/import/results/whiteblacklisting/${processId}`
                },
                pageCount: 1,
                pageIndex: 0,
                pageSize: 10,
                show: true,
                type: 'full'
              },
              type: 'list'
            }}
            isSelectableList={false}
            showColumnsHeader
          />
        ) : (
          <GenericImportDetail rows={preview?.rows ?? []} />
        )}
        <div className="appAndSiteMarketplaceImport__footer">
          <ButtonUI
            icon="fa-solid fa-arrow-left"
            id="appAndSiteMarketplaceImportConfirmGoBack"
            onClick={handleConfirmGoBackClick}
            text={formatMessage({
              defaultMessage: 'Go back',
              id: 'appAndSiteMarketplace.import.buttons.goBack'
            })}
            type="secondary"
          />
          <ButtonUI
            id="appAndSiteMarketplaceImportSave"
            onClick={handleSaveClick}
            text={formatMessage({
              defaultMessage: 'Confirm & Upload',
              id: 'appAndSiteMarketplace.import.buttons.confirmUpload'
            })}
            type="primary"
          />
        </div>
      </ModalUI>
    </AppAndSiteMarketplaceImportStyled>
  );
}
